import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ModalityBadge from "@/components/app/ModalityBadge";
import { useCart } from "@/lib/cart";
import { EditionSelect, upcomingEditions } from "@/components/app/EditionSelect";
import { toast } from "sonner";
import { ArrowLeft, Clock, Users, ShieldCheck, ShoppingCart, MapPin, Calendar as CalendarIcon } from "lucide-react";

export default function CourseDetail() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const { addItem } = useCart();
  const [editionId, setEditionId] = useState("");

  useEffect(() => { api.get(`/courses/${id}`).then(r => setCourse(r.data)); }, [id]);
  if (!course) return <div className="text-slate-500">Caricamento…</div>;
  const isVideo = course.modality === "videoconferenza";
  const upcoming = upcomingEditions(course.editions);
  const add = () => {
    if (!isVideo) return addItem(course);
    const ed = upcoming.find((e) => e.id === editionId);
    if (!ed) return toast.error("Seleziona l'edizione in programma prima di aggiungere al carrello");
    addItem(course, 1, ed);
  };

  return (
    <div className="space-y-6" data-testid="course-detail">
      <Link to="/partner/catalogo" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Torna al catalogo
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="aspect-[16/8] rounded-2xl overflow-hidden bg-slate-100">
            {course.image_url && <img src={course.image_url} alt={course.title} className="w-full h-full object-cover" />}
          </div>
          <div className="flex gap-2">
            <Badge className="bg-white text-slate-700 border border-slate-200">{course.category}</Badge>
            <ModalityBadge modality={course.modality} testid="course-detail-modality" />
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-slate-900">{course.title}</h1>
          <p className="text-slate-600 leading-relaxed">{course.description}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-4 bg-white rounded-xl border border-slate-200">
              <div className="text-xs uppercase text-amber-600 font-bold tracking-widest">Durata</div>
              <div className="font-display font-bold text-slate-900 mt-1">{course.duration_hours} ore</div>
            </div>
            <div className="p-4 bg-white rounded-xl border border-slate-200">
              <div className="text-xs uppercase text-amber-600 font-bold tracking-widest">Modalità</div>
              <div className="font-display font-bold text-slate-900 mt-1 capitalize">{course.modality}</div>
            </div>
            <div className="p-4 bg-white rounded-xl border border-slate-200">
              <div className="text-xs uppercase text-amber-600 font-bold tracking-widest">Destinatari</div>
              <div className="font-display font-bold text-slate-900 mt-1 text-sm">{course.audience || "Aziende"}</div>
            </div>
            <div className="p-4 bg-white rounded-xl border border-slate-200">
              <div className="text-xs uppercase text-amber-600 font-bold tracking-widest">Validità</div>
              <div className="font-display font-bold text-slate-900 mt-1">{course.validity || "-"}</div>
            </div>
          </div>

          {course.editions && course.editions.length > 0 && (
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <h2 className="font-display text-xl font-bold mb-4">Prossime edizioni</h2>
                <div className="space-y-3">
                  {course.editions.map((e) => (
                    <div key={e.id} className="flex items-center gap-4 p-3 border border-slate-100 rounded-lg" data-testid={`edition-${e.id}`}>
                      <div className="text-center bg-amber-50 rounded-lg p-2 min-w-[56px]">
                        <div className="text-xs uppercase text-amber-700 font-bold">{new Date(e.start_date).toLocaleDateString("it-IT", { month: "short" })}</div>
                        <div className="font-display text-xl font-extrabold text-slate-900">{new Date(e.start_date).getDate()}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900">{dateIt(e.start_date)} · {e.start_time}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-1 flex-wrap"><MapPin className="w-3 h-3" />{e.location || "-"} · Docente: {e.teacher || "-"}</div>
                      </div>
                      {e.calendar_url && (
                        <a
                          href={e.calendar_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm font-semibold text-sky-700 hover:text-sky-900 hover:underline"
                          data-testid={`edition-calendar-link-${e.id}`}
                        >
                          <CalendarIcon className="w-4 h-4" /> Calendario
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card className="border-slate-200 athena-shadow sticky top-6">
            <CardContent className="p-6">
              {typeof course.partner_price_final === "number" && (course.partner_discount_percent > 0 || course.partner_custom_price) ? (
                <>
                  <div className="text-xs uppercase text-amber-600 font-bold tracking-widest">Il tuo prezzo partner</div>
                  <div className="font-display text-4xl font-extrabold text-[#0F2942] mt-1" data-testid="course-detail-price-final">
                    {eur(course.partner_price_final)}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {!course.partner_custom_price && <span className="text-sm text-slate-500 line-through">{eur(course.partner_price)}</span>}
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid="course-detail-discount-badge">
                      {course.partner_custom_price ? "Prezzo dedicato" : `Sconto dedicato -${course.partner_discount_percent}%`}
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-400 line-through mt-1">Prezzo pubblico {eur(course.public_price)}</div>
                </>
              ) : (
                <>
                  <div className="text-xs uppercase text-amber-600 font-bold tracking-widest">Prezzo partner</div>
                  <div className="font-display text-4xl font-extrabold text-[#0F2942] mt-1" data-testid="course-detail-price-final">{eur(course.partner_price)}</div>
                  <div className="text-sm text-slate-400 line-through">Prezzo pubblico {eur(course.public_price)}</div>
                </>
              )}
              {isVideo && <div className="mt-4"><EditionSelect editions={upcoming} value={editionId} onChange={setEditionId} /></div>}
              <Button className="w-full mt-4 h-11 bg-amber-500 hover:bg-amber-600 text-white"
                onClick={add} disabled={isVideo && upcoming.length === 0}
                data-testid="course-detail-add-to-cart">
                <ShoppingCart className="w-4 h-4 mr-2" /> Aggiungi al carrello
              </Button>
              <ul className="mt-5 space-y-2 text-sm text-slate-600">
                <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Attestato di partecipazione</li>
                <li className="flex gap-2"><Users className="w-4 h-4 text-emerald-600" /> Iscrizione multi-corsista</li>
                <li className="flex gap-2"><Clock className="w-4 h-4 text-emerald-600" /> Accesso immediato dopo il pagamento</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
